import { DialogComponent } from '@theme/dialog';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';
import { bindOverflowCarousel } from '@theme/bt-scroll-carousel';

/** The cart drawer reads this off the add event to show the line before Shopify responds. */
const PREVIEW_LINE = Symbol.for('bt-cart-drawer:preview-line');
const QUICK_ADD_DRAWER_SECTION = 'section-rendering-quick-add-drawer';
const DELIVERY_CUTOFF_HOUR = 14;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * @typedef {object} QuickAddVariant
 * @property {number} id
 * @property {boolean} available
 * @property {string} price
 * @property {string | null} compareAtPrice
 * @property {string} url
 * @property {string | null} option1
 * @property {string | null} option2
 * @property {string | null} option3
 * @property {number | null} featuredMediaId
 * @property {string | null} [featuredMediaUrl]
 */

/**
 * @typedef {object} QuickAddPayload
 * @property {number} productId
 * @property {string} url
 * @property {string} [productHandle]
 * @property {string} [title]
 * @property {boolean} [enableNotify]
 * @property {string} addLabel
 * @property {string} [selectSizeLabel]
 * @property {string} [outOfStockLabel]
 * @property {string} soldOutLabel
 * @property {string} [notifyInvalid]
 * @property {string} [notifySending]
 * @property {string} [notifySuccess]
 * @property {string} [notifyError]
 * @property {string | null} [featuredMediaUrl]
 * @property {QuickAddVariant[]} variants
 */

/**
 * Add a variant to cart via Cart AJAX API and theme cart events.
 * @param {HTMLElement} source
 * @param {string} variantId
 * @param {string} [eventSource]
 * @param {{ variantId: string, title?: string, size?: string, price?: string, imageSrc?: string } | null} [preview]
 *   Shown in the cart drawer while the add is in flight.
 */
export async function addVariantToCart(source, variantId, eventSource = 'quick-add-drawer', preview = null) {
  const sectionIds = [];
  for (const item of document.querySelectorAll('cart-items-component, bt-cart-drawer')) {
    if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
  }

  const deferredEventPromise = CartLinesUpdateEvent.createPromise();
  const event = new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: [{ merchandiseId: variantId, quantity: 1 }],
    promise: deferredEventPromise.promise,
  });
  if (preview) event[PREVIEW_LINE] = preview;
  source.dispatchEvent(event);

  const detail = {
    source: eventSource,
    sourceId: source.id?.toString?.() || '',
    productId: source.getAttribute('data-product-id') || '',
  };

  try {
    const response = await fetch(
      Theme.routes.cart_add_url,
      fetchConfig('json', {
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          ...(sectionIds.length && { sections: sectionIds.join(','), sections_url: window.location.pathname }),
        }),
      })
    ).then((res) => res.json());

    if (response.status) {
      source.dispatchEvent(
        new CartErrorEvent({
          error: response.message || Theme.translations?.add_to_cart_error,
          code: 'INVALID',
          detail: { description: response.description, errors: response.errors },
        })
      );

      const ajaxCart = await fetchCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: { ...detail, items: ajaxCart.items, itemCount: ajaxCart.item_count, didError: true },
      });
      return false;
    }

    let cart = null;
    let itemCount = itemCountFromSections(response.sections);
    if (itemCount === null) {
      const ajaxCart = await fetchCart();
      cart = CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart);
      itemCount = ajaxCart.item_count;
    }

    deferredEventPromise.resolve({
      cart,
      detail: {
        ...detail,
        items: response.items ?? [],
        itemCount,
        sections: response.sections ?? {},
        didError: false,
      },
    });
    return true;
  } catch (error) {
    deferredEventPromise.reject(error);
    console.warn('[quick-add-drawer] Add to cart failed:', error);
    return false;
  }
}

function fetchCart() {
  return fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  }).then((res) => res.json());
}

/**
 * @param {Record<string, string> | null | undefined} sections
 * @returns {number | null}
 */
function itemCountFromSections(sections) {
  for (const html of Object.values(sections ?? {})) {
    const match = typeof html === 'string' ? html.match(/data-cart-item-count="(\d+)"/) : null;
    if (match) return Number(match[1]);
  }
  return null;
}

function stripHtml(value) {
  if (!value) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = value;
  return (tmp.textContent || '').trim();
}

/**
 * Format remaining time until a cutoff as "X hours Y minutes".
 * @param {number} ms
 * @returns {string}
 */
function formatRemainingTime(ms) {
  const totalMinutes = Math.max(0, Math.round(ms / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts = [];

  if (hours > 0) {
    const hourLabel = hours === 1 ? Theme.translations?.quick_buy_hour : Theme.translations?.quick_buy_hours;
    if (hourLabel) parts.push(`${hours} ${hourLabel}`);
  }

  if (minutes > 0 || hours === 0) {
    const minuteLabel = minutes === 1 ? Theme.translations?.quick_buy_minute : Theme.translations?.quick_buy_minutes;
    if (minuteLabel) parts.push(`${minutes} ${minuteLabel}`);
  }

  return parts.join(' ');
}

/**
 * @param {HTMLElement} root
 * @returns {QuickAddPayload | null}
 */
function readPayload(root) {
  const script = root.querySelector('script[data-quick-add-payload]');
  if (!(script instanceof HTMLScriptElement)) return null;

  try {
    return JSON.parse(script.textContent || '{}');
  } catch {
    return null;
  }
}

/**
 * Quick-buy drawer: mobile bottom sheet, desktop right-slide.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} content
 *
 * @extends {DialogComponent}
 */
class QuickAddDrawer extends DialogComponent {
  requiredRefs = ['dialog', 'content'];
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Map<string, string>} */
  #cachedContent = new Map();

  /** @type {QuickAddPayload | null} */
  #payload = null;

  /** @type {Record<number, string>} */
  #selectedOptions = {};

  /** @type {import('./bt-scroll-carousel').OverflowCarouselBinding | null} */
  #carousel = null;

  /** @type {number | undefined} */
  #notifyTimer;

  /** @type {number} */
  #openId = 0;

  /** Colour requested by the opener (Nosto). Empty after it has been applied. */
  #preferredColour = '';

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    document.addEventListener('submit', this.#onNotifySubmit, true);
    document.addEventListener('input', this.#onNotifyInput, true);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('submit', this.#onNotifySubmit, true);
    document.removeEventListener('input', this.#onNotifyInput, true);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    this.#carousel?.disconnect();
    this.#carousel = null;
    this.#abortController?.abort();
    if (this.#notifyTimer) window.clearTimeout(this.#notifyTimer);
  }

  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Open the drawer for a product URL (mix-and-match rows, etc).
   * @param {string} productUrl
   * @param {HTMLElement | null} [card]
   * @param {{ colour?: string }} [options]
   */
  async openForProduct(productUrl, card = null, options = {}) {
    if (!productUrl) return;

    this.#preferredColour = String(options.colour || '').trim();
    const openId = ++this.#openId;
    const cached = this.#readCachedContent(productUrl, card);
    if (cached) {
      this.#applyContent(cached, card);
      this.showDialog();
      return;
    }

    this.#showLoading();
    this.showDialog();

    const content = await this.#fetchContent(productUrl, card);
    if (openId !== this.#openId) return;
    if (!content) {
      if (this.refs.dialog.open) this.closeDialog();
      return;
    }

    this.#applyContent(content, card);
  }

  /**
   * Open the drawer for a product card.
   * @param {HTMLElement & { productPageUrl?: string }} card
   */
  async openForCard(card) {
    const productUrl = card.productPageUrl || card.querySelector('a[href]')?.getAttribute('href') || '';
    await this.openForProduct(productUrl, card);
  }

  /**
   * @param {string} productUrl
   * @param {HTMLElement | null} [card]
   * @returns {Element | null}
   */
  #readCachedContent(productUrl, card) {
    const cached = this.#cachedContent.get(this.#buildRequestUrl(productUrl, card));
    if (!cached) return null;
    return new DOMParser().parseFromString(cached, 'text/html').querySelector('[data-quick-add-drawer-content]');
  }

  #showLoading() {
    const template = this.querySelector('[data-quick-add-loading]');
    if (!(template instanceof HTMLTemplateElement)) return;
    this.refs.content.replaceChildren(template.content.cloneNode(true));
    this.refs.content.setAttribute('aria-busy', 'true');
  }

  /**
   * @param {Element} content
   * @param {HTMLElement | null} [card]
   */
  #applyContent(content, card) {
    this.refs.content.replaceChildren(document.importNode(content, true));
    this.refs.content.removeAttribute('aria-busy');
    this.#bindContent(card);
  }

  async #fetchContent(productUrl, card) {
    const cacheKey = this.#buildRequestUrl(productUrl, card);

    const cached = this.#cachedContent.get(cacheKey);
    if (cached) {
      const cachedDoc = new DOMParser().parseFromString(cached, 'text/html');
      return cachedDoc.querySelector('[data-quick-add-drawer-content]');
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(cacheKey, { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const content = doc.querySelector('[data-quick-add-drawer-content]');
      if (!(content instanceof HTMLElement)) return null;

      this.#cachedContent.set(cacheKey, content.outerHTML);
      return content;
    } catch (error) {
      if (error?.name === 'AbortError') return null;
      console.warn('[quick-add-drawer] Failed to fetch product:', error);
      return null;
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * @param {string} productUrl
   * @param {HTMLElement | null} [card]
   */
  #buildRequestUrl(productUrl, card) {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('section_id', QUICK_ADD_DRAWER_SECTION);
    url.searchParams.delete('view');

    const checked = card?.querySelector?.('input[type="radio"][data-option-value-id]:checked');
    if (checked instanceof HTMLInputElement && checked.dataset.optionValueId) {
      url.searchParams.set('option_values', checked.dataset.optionValueId);
    }

    return url.toString();
  }

  /**
   * @param {HTMLElement | null} [card]
   */
  #bindContent(card) {
    const root = this.refs.content.querySelector('[data-quick-add-drawer-content]');
    if (!(root instanceof HTMLElement)) return;

    this.#payload = readPayload(root);
    this.#selectedOptions = {};
    this.#carousel?.disconnect();
    this.#carousel = null;

    root.querySelectorAll('.quick-add-drawer__image').forEach((img) => {
      if (!(img instanceof HTMLImageElement)) return;
      img.loading = 'eager';
      img.decoding = 'async';
    });

    root.querySelectorAll('[data-colour-action][data-selected], [data-size-action][data-selected]').forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      const position = Number(button.dataset.optionPosition || 0);
      const value = button.dataset.optionValue || '';
      if (position && value) this.#selectedOptions[position] = value;
    });

    if (card) this.#syncColourFromCard(card, root);
    this.#applyPreferredColour(root);

    const gallery = root.querySelector('.quick-add-drawer__gallery');
    if (gallery instanceof HTMLElement) {
      this.#carousel = bindOverflowCarousel({
        root,
        track: gallery,
        prevButton: root.querySelector('[data-qa-prev]'),
        nextButton: root.querySelector('[data-qa-next]'),
        cardSelector: '.quick-add-drawer__media',
        progressVar: '--bt-qa-progress',
        features: { progress: true, nav: true },
      });
    }

    this.#refreshSizeAvailability(root);
    this.#updateVariantState(root);
    this.#updateDelivery(root);
  }

  /**
   * @param {HTMLElement} card
   * @param {HTMLElement} root
   */
  #syncColourFromCard(card, root) {
    const checked = card.querySelector('input[type="radio"][data-option-value-id]:checked');
    if (!(checked instanceof HTMLInputElement)) return;

    const optionValueId = checked.dataset.optionValueId;
    if (!optionValueId) return;

    const match = root.querySelector(`[data-colour-action][data-option-value-id="${CSS.escape(optionValueId)}"]`);
    if (match instanceof HTMLElement && !match.hasAttribute('data-selected')) {
      this.#selectColour(root, match);
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const sizeGuide = target.closest('[data-quick-add-size-guide]');
    if (sizeGuide instanceof HTMLElement) {
      event.preventDefault();
      const drawer = document.querySelector('bt-size-guide-drawer');
      if (drawer && typeof drawer.showDialog === 'function') drawer.showDialog();
      return;
    }

    const siblingColour = target.closest('[data-colour-sibling]');
    if (siblingColour instanceof HTMLElement) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (siblingColour.hasAttribute('data-selected')) return;
      const siblingUrl = siblingColour.dataset.productUrl || '';
      if (siblingUrl) {
        this.openForProduct(siblingUrl, null, { colour: siblingColour.dataset.colourName || '' });
      }
      return;
    }

    const colourButton = target.closest('[data-colour-action]');
    if (colourButton instanceof HTMLElement) {
      event.preventDefault();
      const root = colourButton.closest('[data-quick-add-drawer-content]');
      if (root instanceof HTMLElement) this.#selectColour(root, colourButton);
      return;
    }

    const sizeButton = target.closest('[data-size-action]');
    if (sizeButton instanceof HTMLButtonElement) {
      event.preventDefault();
      const root = sizeButton.closest('[data-quick-add-drawer-content]');
      if (root instanceof HTMLElement) {
        this.#selectSize(root, sizeButton);
        if (sizeButton.dataset.available === 'false' && this.#payload?.enableNotify) {
          this.#openNotify(root);
        }
      }
      return;
    }

    const notifyButton = target.closest('[data-notify]');
    if (notifyButton instanceof HTMLElement) {
      event.preventDefault();
      const root = notifyButton.closest('[data-quick-add-drawer-content]');
      if (root instanceof HTMLElement) this.#openNotify(root);
      return;
    }

    const addButton = target.closest('[data-add]');
    if (addButton instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#handleCta(addButton);
      return;
    }

    const wishlistButton = target.closest('[data-wishlist]');
    if (wishlistButton instanceof HTMLElement) {
      event.preventDefault();
      const productId = wishlistButton.dataset.productId || '';
      document.dispatchEvent(
        new CustomEvent('wishlist:toggle', {
          bubbles: true,
          detail: { productId, button: wishlistButton },
        })
      );
    }
  };

  /**
   * Select the colour the opener asked for. Sibling colours load that product.
   * @param {HTMLElement} root
   */
  #applyPreferredColour(root) {
    const wanted = this.#preferredColour.toLowerCase();
    this.#preferredColour = '';
    if (!wanted) return;

    const current = root.querySelector('[data-colour-name]')?.textContent?.trim().toLowerCase() || '';
    if (current === wanted) return;

    const siblings = root.querySelectorAll('[data-colour-sibling]');
    for (const button of siblings) {
      if (!(button instanceof HTMLElement)) continue;
      const name = (button.dataset.colourName || '').trim().toLowerCase();
      if (name !== wanted) continue;
      const siblingUrl = button.dataset.productUrl || '';
      if (!siblingUrl || button.hasAttribute('data-selected')) return;
      const currentPath = productPath(root.dataset.productUrl || '');
      if (productPath(siblingUrl) === currentPath) return;
      this.openForProduct(siblingUrl, null, { colour: button.dataset.colourName || '' });
      return;
    }

    const options = root.querySelectorAll('[data-colour-action]');
    for (const button of options) {
      if (!(button instanceof HTMLElement)) continue;
      const name = (button.dataset.optionValue || '').trim().toLowerCase();
      if (name !== wanted || button.hasAttribute('data-selected')) continue;
      this.#selectColour(root, button);
      return;
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLElement} button
   */
  #selectColour(root, button) {
    const position = Number(button.dataset.optionPosition || 0);
    const value = button.dataset.optionValue || '';
    if (!position || !value) return;

    this.#selectedOptions[position] = value;

    root.querySelectorAll('[data-colour-action]').forEach((el) => {
      const selected = el === button;
      el.toggleAttribute('data-selected', selected);
      el.setAttribute('aria-checked', selected ? 'true' : 'false');
    });

    const colourName = root.querySelector('[data-colour-name]');
    if (colourName) colourName.textContent = value.toLowerCase();

    const mediaId = button.dataset.mediaId;
    if (mediaId) {
      const media = root.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`);
      media?.scrollIntoView({ inline: 'start', block: 'nearest', behavior: 'smooth' });
    }

    this.#refreshSizeAvailability(root);
    this.#updateVariantState(root);
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLButtonElement} button
   */
  #selectSize(root, button) {
    const position = Number(button.dataset.optionPosition || 0);
    const value = button.dataset.optionValue || '';
    if (!position || !value) return;

    this.#selectedOptions[position] = value;

    const group = button.closest('[data-size-group]');
    group?.querySelectorAll('[data-size-action]').forEach((el) => {
      if (!(el instanceof HTMLButtonElement)) return;
      this.#paintSizeButton(el, el === button);
    });

    this.#updateVariantState(root);
  }

  /**
   * @param {HTMLElement} root
   */
  #refreshSizeAvailability(root) {
    const variants = this.#payload?.variants || [];

    root.querySelectorAll('[data-size-group]').forEach((group) => {
      if (!(group instanceof HTMLElement)) return;
      const position = Number(group.dataset.optionPosition || 0);
      let firstAvailable = /** @type {HTMLButtonElement | null} */ (null);
      let selectedStillAvailable = false;

      group.querySelectorAll('[data-size-action]').forEach((button) => {
        if (!(button instanceof HTMLButtonElement)) return;
        const value = button.dataset.optionValue || '';
        const match =
          variants.find((variant) => {
            if (variant[`option${position}`] !== value) return false;
            return this.#matchesOtherOptions(variant, position);
          }) || null;
        const available = Boolean(match?.available);

        button.dataset.available = String(available);
        button.dataset.variantId = match?.id ? String(match.id) : '';
        if (match?.price) button.dataset.variantPrice = match.price;
        const imageUrl = match?.featuredMediaUrl || this.#payload?.featuredMediaUrl || '';
        if (imageUrl) button.dataset.variantImage = imageUrl;
        button.setAttribute(
          'aria-label',
          available ? value : `${value} - ${Theme.translations?.inventory_out_of_stock || 'Out of stock'}`
        );

        const bell = button.querySelector('.quick-add-drawer__bell');
        if (bell instanceof HTMLElement) bell.hidden = available;

        const selected = button.hasAttribute('data-selected');
        this.#paintSizeButton(button, selected);

        if (available && !firstAvailable) firstAvailable = button;
        if (selected) selectedStillAvailable = true;
      });

      if (!selectedStillAvailable && firstAvailable) {
        this.#selectSize(root, firstAvailable);
      }
    });
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {boolean} selected
   */
  #paintSizeButton(button, selected) {
    const available = button.dataset.available !== 'false';
    button.toggleAttribute('data-selected', selected);
    button.setAttribute('aria-checked', selected ? 'true' : 'false');
    button.classList.toggle('quick-add-drawer__size--selected', selected);
    button.classList.toggle('quick-add-drawer__size--unavailable', !available);
    button.classList.toggle('text-p3-bold', selected);
    button.classList.toggle('color-text-primary', selected || available);
    button.classList.toggle('color-text-subdued', !available && !selected);
  }

  /**
   * @param {QuickAddVariant} variant
   * @param {number} skipPosition
   */
  #matchesOtherOptions(variant, skipPosition) {
    return Object.entries(this.#selectedOptions).every(([pos, value]) => {
      const position = Number(pos);
      if (position === skipPosition) return true;
      return variant[`option${position}`] === value;
    });
  }

  /**
   * @returns {QuickAddVariant | null}
   */
  #resolveVariant() {
    const variants = this.#payload?.variants || [];
    const selectedCount = Object.keys(this.#selectedOptions).length;
    if (!selectedCount) {
      return variants.find((variant) => variant.available) || variants[0] || null;
    }

    return (
      variants.find((variant) => {
        return Object.entries(this.#selectedOptions).every(([pos, value]) => {
          return variant[`option${Number(pos)}`] === value;
        });
      }) || null
    );
  }

  /**
   * @param {HTMLElement} root
   */
  #updateVariantState(root) {
    const variant = this.#resolveVariant();
    const price = root.querySelector('[data-price]');
    const compare = root.querySelector('[data-compare]');
    const details = root.querySelector('[data-details-link]');
    const addButton = root.querySelector('[data-add]');
    const mode = this.#actionMode(root, variant);

    if (price && variant?.price) price.textContent = stripHtml(variant.price);

    if (compare instanceof HTMLElement) {
      if (variant?.compareAtPrice) {
        compare.textContent = stripHtml(variant.compareAtPrice);
        compare.hidden = false;
      } else {
        compare.hidden = true;
        compare.textContent = '';
      }
    }

    if (details instanceof HTMLAnchorElement && variant?.url) {
      details.href = variant.url;
    }

    if (addButton instanceof HTMLButtonElement && this.#payload) {
      const notifyEnabled = Boolean(this.#payload.enableNotify);
      addButton.dataset.actionMode = mode;
      addButton.disabled = mode === 'unavailable' && !notifyEnabled;
      addButton.textContent =
        mode === 'select-size'
          ? this.#payload.selectSizeLabel || this.#payload.addLabel
          : mode === 'unavailable'
            ? this.#payload.outOfStockLabel || this.#payload.soldOutLabel
            : this.#payload.addLabel;
      if (variant?.id) addButton.dataset.variantId = String(variant.id);
    }

    const notify = root.querySelector('[data-notify]');
    if (notify instanceof HTMLElement) {
      notify.hidden = mode !== 'unavailable' || !this.#payload?.enableNotify;
    }

    const delivery = root.querySelector('[data-delivery]');
    if (delivery instanceof HTMLElement) {
      delivery.hidden = mode !== 'add';
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {QuickAddVariant | null} [variant]
   * @returns {'add' | 'select-size' | 'unavailable'}
   */
  #actionMode(root, variant = this.#resolveVariant()) {
    const hasSizeGroup = Boolean(root.querySelector('[data-size-group]'));
    const selectedSize = root.querySelector('[data-size-action][data-selected]');
    if (hasSizeGroup && !selectedSize) return 'select-size';
    if (!variant || variant.available === false) return 'unavailable';
    return 'add';
  }

  /**
   * @param {HTMLElement} root
   */
  #updateDelivery(root) {
    const delivery = root.querySelector('[data-delivery]');
    if (!(delivery instanceof HTMLElement)) return;

    const template = Theme.translations?.quick_buy_delivery_html;
    if (!template) return;

    const parts = {};
    for (const part of new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      weekday: 'long',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date())) {
      if (part.type !== 'literal') parts[part.type] = part.value;
    }

    const hour = Number(parts.hour);
    const minute = Number(parts.minute);
    const second = Number(parts.second);
    const nowMinutes = hour * 60 + minute + second / 60;
    const cutoffMinutes = DELIVERY_CUTOFF_HOUR * 60;

    let dayLabel = Theme.translations?.quick_buy_tomorrow;
    let remainingMinutes = cutoffMinutes - nowMinutes;

    if (remainingMinutes <= 0) {
      remainingMinutes += 24 * 60;
      const deliveryDate = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day) + 2));
      dayLabel = new Intl.DateTimeFormat('en-GB', { weekday: 'long', timeZone: 'UTC' })
        .format(deliveryDate)
        .toLowerCase();
    }

    const timeLabel = formatRemainingTime(remainingMinutes * 60000);
    delivery.innerHTML = template.replace('%day%', dayLabel).replace('%time%', timeLabel);
    delivery.hidden = this.#actionMode(root) !== 'add';
  }

  /**
   * @param {HTMLButtonElement} button
   */
  #handleCta(button) {
    const root = button.closest('[data-quick-add-drawer-content]');
    if (!(root instanceof HTMLElement)) return;

    const mode = this.#actionMode(root);
    if (mode === 'select-size') {
      root.querySelector('[data-size-group]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (mode === 'unavailable') {
      if (this.#payload?.enableNotify) this.#openNotify(root);
      return;
    }

    const variant = this.#resolveVariant();
    const variantId = variant?.id ? String(variant.id) : button.dataset.variantId;
    if (!variantId || !variant?.available) return;

    button.disabled = true;
    this.closeDialog();
    addVariantToCart(root, variantId).then((added) => {
      if (!added) button.disabled = false;
    });
  }

  /**
   * @returns {HTMLElement | null}
   */
  #notifyDrawer() {
    return document.querySelector('[data-quick-add-notify]');
  }

  /**
   * @param {HTMLElement} root
   */
  #openNotify(root) {
    if (!this.#payload?.enableNotify) return;
    this.#syncNotifyDrawer(root);
    const drawer = this.#notifyDrawer();
    if (drawer && typeof drawer.showDialog === 'function') drawer.showDialog();
  }

  /**
   * @param {HTMLElement} root
   */
  #syncNotifyDrawer(root) {
    const drawer = this.#notifyDrawer();
    if (!drawer) return;

    const variant = this.#resolveVariant();
    const selectedSize = root.querySelector('[data-size-action][data-selected]');
    const size = selectedSize instanceof HTMLElement ? selectedSize.dataset.optionValue || '' : '';
    const colour = root.querySelector('[data-colour-name]')?.textContent?.trim() || '';
    const title = this.#payload?.title || '';
    const notifyTitle =
      colour && title && !title.toLowerCase().includes(colour.toLowerCase()) ? `${title} in ${colour}` : title;

    const name = drawer.querySelector('[data-bt-pdp-notify-title]');
    if (name) name.textContent = notifyTitle;

    const sizeOut = drawer.querySelector('[data-bt-pdp-notify-size]');
    const sizeWrap = drawer.querySelector('[data-bt-pdp-notify-size-wrap]');
    if (sizeOut) sizeOut.textContent = size.toLowerCase();
    if (sizeWrap instanceof HTMLElement) sizeWrap.hidden = size === '';

    const priceOut = drawer.querySelector('[data-bt-pdp-notify-price]');
    const price =
      (selectedSize instanceof HTMLElement && selectedSize.dataset.variantPrice) || variant?.price || '';
    if (priceOut) priceOut.textContent = stripHtml(price);

    const image = drawer.querySelector('[data-bt-pdp-notify-image]');
    const imageSrc =
      (selectedSize instanceof HTMLElement && selectedSize.dataset.variantImage) ||
      variant?.featuredMediaUrl ||
      this.#payload?.featuredMediaUrl ||
      '';
    if (image instanceof HTMLImageElement) {
      if (imageSrc) {
        image.src = imageSrc;
        image.alt = notifyTitle;
        image.hidden = false;
      } else {
        image.hidden = true;
      }
    }

    const email = drawer.querySelector('input[type="email"]');
    if (email instanceof HTMLInputElement) {
      email.setCustomValidity('');
      email.removeAttribute('aria-invalid');
      email.closest('.bt-newsletter-field')?.classList.remove('bt-newsletter-field--invalid');
    }
    this.#setNotifyStatus('', false);
  }

  /**
   * @param {Event} event
   */
  #onNotifyInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const form = input.closest('[data-bt-pdp-notify-form]');
    if (!form?.closest('[data-quick-add-notify]')) return;

    input.setCustomValidity('');
    input.removeAttribute('aria-invalid');
    input.closest('.bt-newsletter-field')?.classList.remove('bt-newsletter-field--invalid');
    this.#setNotifyStatus('', false);
  };

  /**
   * @param {Event} event
   */
  #onNotifySubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.closest('[data-quick-add-notify]')) return;
    if (!form.matches('[data-bt-pdp-notify-form]')) return;
    event.preventDefault();
    this.#submitNotify(form);
  };

  /**
   * @param {HTMLFormElement} form
   */
  #submitNotify(form) {
    const input = form.querySelector('input[type="email"]');
    const email = input instanceof HTMLInputElement ? input.value.trim() : '';
    const submit = form.querySelector('button[type="submit"]');
    const labels = this.#payload || {};

    if (!EMAIL_PATTERN.test(email)) {
      const message = labels.notifyInvalid || 'Enter a valid email';
      if (input instanceof HTMLInputElement) {
        input.setCustomValidity(message);
        input.setAttribute('aria-invalid', 'true');
        input.closest('.bt-newsletter-field')?.classList.add('bt-newsletter-field--invalid');
        input.reportValidity();
        input.focus();
      }
      this.#setNotifyStatus(message, true, 'error');
      return;
    }

    if (input instanceof HTMLInputElement) {
      input.setCustomValidity('');
      input.removeAttribute('aria-invalid');
      input.closest('.bt-newsletter-field')?.classList.remove('bt-newsletter-field--invalid');
    }
    if (submit instanceof HTMLButtonElement) submit.disabled = true;
    this.#setNotifyStatus(labels.notifySending || 'Sending…', true, 'loading');

    const root = this.refs.content.querySelector('[data-quick-add-drawer-content]');
    const selectedSize =
      root instanceof HTMLElement ? root.querySelector('[data-size-action][data-selected]') : null;
    const payload = {
      email,
      productId: this.#payload?.productId ? String(this.#payload.productId) : '',
      productHandle: this.#payload?.productHandle || '',
      variantId: selectedSize instanceof HTMLElement ? selectedSize.dataset.variantId || '' : '',
      colour: root instanceof HTMLElement ? root.querySelector('[data-colour-name]')?.textContent?.trim() || '' : '',
      size: selectedSize instanceof HTMLElement ? selectedSize.dataset.optionValue || '' : '',
    };

    let settled = false;
    const finish = (ok, message) => {
      if (settled) return;
      settled = true;
      if (submit instanceof HTMLButtonElement) submit.disabled = false;
      this.#setNotifyStatus(
        message || (ok ? labels.notifySuccess || '' : labels.notifyError || ''),
        true,
        ok ? 'success' : 'error'
      );
    };

    form.dispatchEvent(
      new CustomEvent('bt-pdp:stock-notify', {
        bubbles: true,
        cancelable: true,
        detail: {
          ...payload,
          setResult: (status, message) => finish(status === 'success', message),
        },
      })
    );

    this.#notifyTimer = window.setTimeout(() => {
      if (!settled) finish(true, labels.notifySuccess || '');
    }, 400);
  }

  /**
   * @param {string} message
   * @param {boolean} visible
   * @param {'error' | 'loading' | 'success' | ''} [kind]
   */
  #setNotifyStatus(message, visible, kind = '') {
    const status = this.#notifyDrawer()?.querySelector('[data-bt-pdp-notify-status]');
    if (!(status instanceof HTMLElement)) return;
    status.hidden = !visible;
    status.textContent = message;
    status.dataset.state = kind;
  }
}

/**
 * @param {string} url
 */
function productPath(url) {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url;
  }
}

if (!customElements.get('quick-add-drawer')) {
  customElements.define('quick-add-drawer', QuickAddDrawer);
}
